import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ApolloError } from '@apollo/client';
import { OptionType } from '@/common/presentation/components/forms/fields/FormikAutocomplete';
import debounce from 'lodash-es/debounce';

/**
 * Extended option type that includes additional fields commonly used in autocomplete components
 */
export interface AutocompleteOption extends OptionType {
  id?: number;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
}

/**
 * Result object returned by the useAutocomplete hook
 */
export interface UseAutocompleteResult {
  options: AutocompleteOption[];
  loading: boolean;
  error: Error | null;
  filterOptions: (options: AutocompleteOption[], state: { inputValue: string }) => AutocompleteOption[];
  refetch?: () => Promise<unknown>;
}

/**
 * Props for the useAutocomplete hook
 * @template T - The type of the items returned by the query
 */
interface UseAutocompleteProps<T> {
  /** The current search term */
  searchTerm: string;
  /** Debounce time in milliseconds for search term updates */
  debounceMs?: number;
  /** Callback when an option is selected */
  onSelect?: (option: AutocompleteOption) => void;
  /** Function that executes the query with the current search term */
  query: (search: string) => {
    data?: { items?: (T | null)[] | null } | null;
    loading: boolean;
    error?: ApolloError | Error | null;
    refetch?: () => Promise<unknown>;
  };
  /** Function that transforms a query result item into an AutocompleteOption */
  transform: (item: T) => AutocompleteOption;
}

/**
 * A reusable hook for implementing autocomplete functionality with Apollo GraphQL queries
 *
 * @template T - The type of the items returned by the query
 * @param props - Configuration options for the hook
 * @returns Object containing autocomplete options, loading state, error state, and filter function
 *
 * @example
 * ```tsx
 * const { options, loading, error, filterOptions } = useAutocomplete({
 *   searchTerm: 'search term',
 *   debounceMs: 300,
 *   onSelect: (option) => console.log('Selected:', option),
 *   query: (search) => useMyQuery({ variables: { search } }),
 *   transform: (item) => ({
 *     value: item.id,
 *     label: item.name,
 *     email: item.email,
 *   }),
 * });
 * ```
 */
export const useAutocomplete = <T>({
  searchTerm = '',
  debounceMs = 1000,
  onSelect,
  query,
  transform
}: UseAutocompleteProps<T>): UseAutocompleteResult => {
  const [options, setOptions] = useState<AutocompleteOption[]>([]);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const prevSearchTermRef = useRef(searchTerm);
  const prevOptionsRef = useRef<AutocompleteOption[]>([]);

  // Memoize the debounced function to prevent recreation on every render
  const debouncedSetSearchTerm = useMemo(() => debounce((value: string) => setDebouncedSearchTerm(value), debounceMs), [debounceMs]);

  // Only update debounced search term if the actual search term changed
  useEffect(() => {
    if (searchTerm !== prevSearchTermRef.current) {
      prevSearchTermRef.current = searchTerm;
      debouncedSetSearchTerm(searchTerm);
    }
    return () => debouncedSetSearchTerm.cancel();
  }, [searchTerm, debouncedSetSearchTerm]);

  // Execute query with debounced search term
  const { data, loading, error, refetch } = query(debouncedSearchTerm);

  // Transform and update options only when data changes
  useEffect(() => {
    if (data?.items) {
      const formattedOptions = data.items.filter((item): item is T => item !== null).map(transform);

      // Only update if options actually changed
      if (JSON.stringify(formattedOptions) !== JSON.stringify(prevOptionsRef.current)) {
        prevOptionsRef.current = formattedOptions;
        setOptions(formattedOptions);
      }
    }
  }, [data, transform]);

  // Memoize filter function to prevent recreation on every render
  const filterOptions = useCallback((options: AutocompleteOption[], { inputValue }: { inputValue: string }) => {
    const searchLower = inputValue.toLowerCase();
    return options.filter((option) => {
      const labelMatches = option.label.toLowerCase().includes(searchLower);
      const emailMatches = option.email?.toLowerCase().includes(searchLower) ?? false;
      const phoneMatches = option.phone?.toLowerCase().includes(searchLower) ?? false;
      const addressMatches = option.address?.toLowerCase().includes(searchLower) ?? false;
      return labelMatches || emailMatches || phoneMatches || addressMatches;
    });
  }, []);

  // Call onSelect when options become available
  useEffect(() => {
    if (onSelect && options.length > 0 && options[0]) {
      onSelect(options[0]);
    }
  }, [options, onSelect]);

  // Memoize the result to prevent unnecessary re-renders
  return useMemo(
    () => ({
      options,
      loading,
      error: error as Error | null,
      filterOptions,
      refetch: refetch || undefined
    }),
    [options, loading, error, filterOptions, refetch]
  );
};
